import { TextEllipsis, TextEllipsisWithTooltip } from 'components/Kizen/Table';
import {
  SizedCell,
  SortableHeadCell,
  ActionSizedCell,
} from 'components/Kizen/Table/CellLineHeightStyles';
import format from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import LinkTextInline from 'components/Inputs/inline/LinkText';
import PhoneNumberInlineField from 'components/Fields/InlineTableFieldInput/PhoneNumber';
import TextInputInline from 'components/Inputs/inline/TextInput';
import { getColumnWidth } from 'components/Fields/InlineTableFieldInput';
import TeamMemberRolesInlineField from 'components/Fields/InlineTableFieldInput/TeamMemberRoles';
import TeamMemberPermissionGroupsInlineField from 'components/Fields/InlineTableFieldInput/TeamMemberPermissionGroups';
import { getCellForFieldType } from '__components/Layout/BigTableLayout/InlineCells';
import ActionCell from 'components/Tables/ActionCell';
import RowMenuCell from '__components/Tables/Big/NewRowMenuCell';
import DateInputInline, {
  DateInputInlineSelect,
} from 'components/Inputs/inline/DateInput';
import ClearSelectButton from 'components/Inputs/Select/ClearButton';
import { ApplySelectOverlayButton } from 'components/Inputs/SelectOverlay';
import PriceNumberInline from 'components/Inputs/inline/TextInput/presets/PriceNumber';
import { toastVariant } from 'components/ToastProvider';
import { ColumnsIconCell } from 'pages/Common/components/BigTableCells';
import { REGISTERED_ROLE_AND_GROUP_NAME } from 'pages/Settings/helpers';
import CustomObjectsService from 'services/CustomObjectsService';

const errorData = (message) => ({
  variant: toastVariant.FAILURE,
  delay: 3000,
  message,
});

const textWithTruncation = (value) => (
  <TextEllipsisWithTooltip>{value}</TextEllipsisWithTooltip>
);

const NumberCell = ({ column, data }) => {
  const { id } = column;

  return (
    <SizedCell minWidth="100px" padding="30px">
      <TextEllipsis>{data[id]}</TextEllipsis>
    </SizedCell>
  );
};

const DateTimeCell = ({ column, data }) => {
  const { id } = column;

  return (
    <SizedCell minWidth="140px" padding="30px">
      {Boolean(data[id]) && (
        <TextEllipsis>{format(data[id], 'MM/dd/yyyy hh:mm a')}</TextEllipsis>
      )}
    </SizedCell>
  );
};

const RoleActionCell = ({ access, adminPermissionRole, ...props }) => {
  const { t } = useTranslation();
  const {
    data: { name, id },
  } = props;
  return (
    <ActionCell
      title={t('Edit Role')}
      menuType="absolute"
      options={[
        adminPermissionRole?.id !== id && { value: 'edit', label: t('Edit') },
        { value: 'duplicate', label: t('Duplicate') },
        access.roles.edit && {
          value: 'apply-toolbar',
          label: t('Apply Toolbar'),
        },
        access.roles.edit && {
          value: 'apply-columns',
          label: t('Apply Column Template'),
        },
        access.roles.edit && {
          value: 'apply-quick-filters',
          label: t('Apply Quick Filter Template'),
        },
        {
          value: 'history',
          label: t('View History'),
        },
        adminPermissionRole?.id !== id &&
          access.roles.remove &&
          name !== REGISTERED_ROLE_AND_GROUP_NAME && {
            value: 'delete',
            label: t('Delete'),
          },
      ].filter(Boolean)}
      {...props}
    />
  );
};

const PermissionGroupActionCell = ({
  access,
  adminPermissionGroup,
  ...props
}) => {
  const { t } = useTranslation();
  const {
    data: { name, id },
  } = props;

  return (
    <ActionCell
      title={t('Edit Permission Group')}
      menuType="absolute"
      options={[
        adminPermissionGroup?.id !== id && { value: 'edit', label: t('Edit') },
        { value: 'duplicate', label: t('Duplicate') },
        { value: 'history', label: t('View History') },
        // KZN-8714 suppressing this option untill the same functionality is implemented for toolbar templates
        // access.permissionGroups.edit && {
        //   value: 'apply-toolbar',
        //   label: t('Apply Toolbar'),
        // },
        // access.permissionGroups.edit && {
        //   value: 'apply-columns',
        //   label: t('Apply Column Template'),
        // },
        // access.permissionGroups.edit && {
        //   value: 'apply-quick-filters',
        //   label: t('Apply Quick Filter Template'),
        // },
        adminPermissionGroup?.id !== id &&
          access.permissionGroups.remove &&
          name !== REGISTERED_ROLE_AND_GROUP_NAME && {
            value: 'delete',
            label: t('Delete'),
          },
      ].filter(Boolean)}
      {...props}
    />
  );
};

const ActionHeadCell = (props) => {
  const { t } = useTranslation();
  return (
    <ActionSizedCell {...props}>
      <TextEllipsisWithTooltip as="span" weight="bold">
        {t('Actions')}
      </TextEllipsisWithTooltip>
    </ActionSizedCell>
  );
};

export const getTeamCustomFieldColumns = ({ fields, model, onSubmit }) => {
  return fields.map((field) => {
    const width = `${getColumnWidth(field)}px`;

    return {
      id: field.name,
      label: field.displayName,
      headCell: (
        <SortableHeadCell
          label={field.displayName}
          padding="30px"
          width={width}
        />
      ),
      bodyCell: {
        field,
        model,
        onSubmit,
        width,
        Component: getCellForFieldType(field.fieldType),
        isCustomObjects: true,
        serviceToUse: CustomObjectsService,
        menuLeftButton: <ClearSelectButton />,
      },
    };
  });
};

export const keyListenerIds = {
  annualSalesGoal: (member) => `annual_sales_goal-${member.id}`,
  fullName: (member) => `full_name-${member.id}`,
  phone: (member) => `phone-${member.id}`,
  permissionGroups: (member) => `permission_groups-${member.id}`,
  roles: (member) => `roles-${member.id}`,
  startDate: (member) => `start_date-${member.id}`,
};

export const getTeamColumns = (
  { onUpdateField, onSelectAction, onEditColumns },
  t,
  preReleaseFeature,
  showToast,
  access,
  chosenBusiness
) => {
  return [
    {
      id: 'spacer',
      headCell: <SizedCell width="20px" />,
      cell: <SizedCell width="20px" />,
    },
    {
      id: 'fullName',
      headCell: (
        <SortableHeadCell
          by="full_name"
          label={t('Team Member')}
          width="140px"
          padding="30px"
        />
      ),
      cell: <SizedCell width="140px" padding="30px" />,
      format: (fullName, member) => {
        const shouldFocusNext = true;

        return preReleaseFeature ? (
          <LinkTextInline
            type="link"
            to={`/team-member/${member.id}/timeline`}
            target="_blank"
            rel="noopener noreferrer"
            keyListenerId={keyListenerIds.fullName(member)}
            value={fullName}
            onSubmit={async (value) => {
              const [firstName, ...lastNames] = (value || '').split(' ');
              await onUpdateField(member, {
                firstName,
                lastName: lastNames.join(' '),
              });
              return shouldFocusNext;
            }}
          />
        ) : (
          <TextInputInline
            value={fullName}
            keyListenerId={keyListenerIds.fullName(member)}
            onSubmit={async (value) => {
              const [firstName, ...lastNames] = (value || '').split(' ');
              await onUpdateField(member, {
                firstName,
                lastName: lastNames.join(' '),
              });
              return shouldFocusNext;
            }}
          />
        );
      },
    },
    {
      id: 'email',
      headCell: (
        <SortableHeadCell label={t('Email')} width="170px" padding="30px" />
      ),
      cell: <SizedCell width="170px" padding="30px" />,
      format: textWithTruncation,
    },
    {
      id: 'lastLogin',
      headCell: (
        <SortableHeadCell
          numeric
          by="last_login"
          label={t('Last Login')}
          width="100px"
          padding="30px"
        />
      ),
      cell: <SizedCell width="100px" padding="30px" />,
      format: (lastLogin, _member) => (
        <DateInputInline readOnly>
          <DateInputInlineSelect value={lastLogin} />
        </DateInputInline>
      ),
    },
    {
      id: 'phone',
      headCell: (
        <SortableHeadCell
          numeric
          label={t('Phone Number')}
          width="125px"
          padding="30px"
        />
      ),
      cell: <SizedCell width="125px" padding="30px" />,
      format: (phone, member) => {
        return (
          <PhoneNumberInlineField
            keyListenerId={keyListenerIds.phone(member)}
            defaultCountry={chosenBusiness.phone_default_region}
            value={phone}
            onSubmit={async (value) => {
              if (!value) {
                await onUpdateField(member, {
                  phone: null,
                });
              } else {
                await onUpdateField(member, {
                  phone: value,
                });
              }
            }}
          />
        );
      },
    },
    access.roles.edit && {
      id: 'roles',
      headCell: (
        <SortableHeadCell label={t('Role(s)')} width="170px" padding="30px" />
      ),
      cell: <SizedCell text width="170px" padding="30px" />,
      format: (roles, member) => (
        <TeamMemberRolesInlineField
          keyListenerId={keyListenerIds.roles(member)}
          value={roles}
          onSubmit={async (value) => {
            try {
              await onUpdateField(member, {
                roles: (value || []).map((role) => role.id),
              });
            } catch (err) {
              const { data } = err.response;
              if (data.statusCode === 400) {
                const [error] = data.errors;
                showToast(errorData(error));
              }
              throw new Error(err);
            }
          }}
          data-field-id="roles"
        />
      ),
    },
    access.permissionGroups.edit && {
      id: 'permissionGroups',
      headCell: (
        <SortableHeadCell
          by="permission_groups"
          label={t('Additional Permission Group(s)')}
          width="220px"
          padding="30px"
        />
      ),
      cell: <SizedCell text width="220px" padding="30px" />,
      format: (permissionGroups, member) => {
        return (
          <TeamMemberPermissionGroupsInlineField
            keyListenerId={keyListenerIds.permissionGroups(member)}
            value={permissionGroups}
            onSubmit={async (value) => {
              try {
                await onUpdateField(member, {
                  permissionGroups: (value || []).map((pg) => pg.id),
                });
              } catch (err) {
                const { data } = err.response;
                if (data.statusCode === 400) {
                  const [error] = data.errors;
                  showToast(errorData(error));
                }
                throw new Error(err);
              }
            }}
            data-field-id="groups"
          />
        );
      },
    },
    {
      id: 'annualSalesGoal',
      headCell: (
        <SortableHeadCell
          numeric
          by="annual_sales_goal"
          label={`$ ${t('Goal')}`}
          width="75px"
          padding="30px"
        />
      ),
      cell: <SizedCell width="75px" padding="30px" />,
      format: (annualSalesGoal, member) => {
        return (
          <PriceNumberInline
            keyListenerId={keyListenerIds.annualSalesGoal(member)}
            value={annualSalesGoal}
            onSubmit={async (value) => {
              await onUpdateField(member, {
                annualSalesGoal: value,
              });
            }}
          />
        );
      },
    },
    {
      id: 'startDate',
      headCell: (
        <SortableHeadCell
          numeric
          by="start_date"
          label={t('Start Date')}
          width="100px"
          padding="30px"
        />
      ),
      cell: <SizedCell width="100px" padding="30px" />,
      format: (startDate, member) => (
        <DateInputInline
          keyListenerId={keyListenerIds.startDate(member)}
          onSubmit={async (value) => {
            await onUpdateField(member, {
              startDate: value,
            });
          }}
          trackExternal
        >
          <DateInputInlineSelect
            value={startDate}
            menuLeftButton={<ClearSelectButton />}
            menuRightButton={<ApplySelectOverlayButton />}
          />
        </DateInputInline>
      ),
    },
    {
      id: 'verified',
      headCell: (
        <SortableHeadCell label={t('Status')} width="65px" padding="35px" />
      ),
      cell: <SizedCell width="65px" padding="35px" />,
      format: (verified) => (
        <TextEllipsis
          color={
            verified
              ? 'var(--color-viz-cat-1-primary)'
              : 'var(--color-viz-cat-3-primary)'
          }
        >
          {verified ? t('Verified') : t('Unverified')}
        </TextEllipsis>
      ),
    },
    {
      id: 'actions',
      headCell: <ColumnsIconCell onClick={onEditColumns} zIndex={2} />,
      bodyCell: {
        isMenuCell: true,
        Component: RowMenuCell,
        isCustomObjects: true,
        title: t('Edit Team Member'),
        onSelect: onSelectAction,
      },
    },
  ].filter(Boolean);
};

const formatNameColumn = (permissionId, onClickName) => (value, pg) => {
  return permissionId === pg.id ? (
    <TextEllipsisWithTooltip>{value}</TextEllipsisWithTooltip>
  ) : (
    <TextEllipsisWithTooltip
      type="link"
      onClick={(event) => {
        // Have to prevent default so the URL does not change and strip query parameters
        event.preventDefault();
        onClickName(pg);
      }}
    >
      {value}
    </TextEllipsisWithTooltip>
  );
};

export const getRoleColumns = (
  { onClickName, onSelectAction },
  t,
  access,
  adminPermissionRole
) => [
  {
    id: 'spacer',
    headCell: <SizedCell width="20px" />,
    cell: <SizedCell width="20px" />,
  },
  {
    id: 'name',
    headCell: (
      <SortableHeadCell label={t('Name')} minWidth="200px" padding="30px" />
    ),
    cell: <SizedCell minWidth="200px" padding="30px" />,
    format: formatNameColumn(adminPermissionRole.id, onClickName),
  },
  {
    id: 'permissionGroups',
    headCell: (
      <SortableHeadCell
        label={t('Default Permission Group(s)')}
        by="permission_groups"
        minWidth="200px"
        padding="30px"
      />
    ),
    cell: <SizedCell minWidth="200px" padding="30px" />,
    format: (pgs) => textWithTruncation(pgs.map(({ name }) => name).join(', ')),
  },
  {
    id: 'userCount',
    headCell: (
      <SortableHeadCell
        numeric
        label={t('User Count')}
        by="user_count"
        minWidth="100px"
        padding="30px"
      />
    ),
    cell: NumberCell,
  },
  {
    id: 'created',
    headCell: (
      <SortableHeadCell
        numeric
        label={t('Created')}
        minWidth="140px"
        padding="30px"
      />
    ),
    cell: DateTimeCell,
  },
  {
    id: 'updated',
    headCell: (
      <SortableHeadCell
        numeric
        label={t('Updated')}
        minWidth="140px"
        padding="30px"
      />
    ),
    cell: DateTimeCell,
  },
  {
    id: 'actions',
    headCell: <ActionHeadCell padding="16px" minWidth="97px" />,
    cell: (
      <RoleActionCell
        padding="16px"
        onSelectAction={onSelectAction}
        access={access}
        adminPermissionRole={adminPermissionRole}
      />
    ),
  },
];

export const getPermissionGroupColumns = (
  { onClickName, onSelectAction },
  t,
  access,
  adminPermissionGroup
) => [
  {
    id: 'spacer',
    headCell: <SizedCell width="20px" />,
    cell: <SizedCell width="20px" />,
  },
  {
    id: 'name',
    headCell: (
      <SortableHeadCell label={t('Name')} minWidth="200px" padding="30px" />
    ),
    cell: <SizedCell minWidth="200px" padding="30px" />,
    format: formatNameColumn(adminPermissionGroup?.id, onClickName),
  },
  {
    id: 'summary',
    headCell: (
      <SortableHeadCell
        numeric
        label={t('Permission(s) Summary')}
        minWidth="200px"
        padding="30px"
      />
    ),
    cell: <SizedCell minWidth="200px" padding="30px" />,
    format: ({ remove = 0, edit = 0, view = 0, none = 0 } = {}) => {
      const text = t(
        '{{remove}} All, {{edit}} Create/Edit, {{view}}  View, {{none}} No Access',
        {
          remove,
          edit,
          view,
          none,
        }
      );
      return textWithTruncation(text);
    },
  },
  {
    id: 'userCount',
    headCell: (
      <SortableHeadCell
        numeric
        label={t('User Count')}
        by="user_count"
        minWidth="100px"
        padding="30px"
      />
    ),
    cell: NumberCell,
  },
  {
    id: 'roleCount',
    headCell: (
      <SortableHeadCell
        numeric
        label={t('Role Count')}
        by="role_count"
        minWidth="100px"
        padding="30px"
      />
    ),
    cell: NumberCell,
  },
  {
    id: 'created',
    headCell: (
      <SortableHeadCell
        numeric
        label={t('Created')}
        minWidth="140px"
        padding="30px"
      />
    ),
    cell: DateTimeCell,
  },
  {
    id: 'updated',
    headCell: (
      <SortableHeadCell
        numeric
        label={t('Updated')}
        minWidth="140px"
        padding="30px"
      />
    ),
    cell: DateTimeCell,
  },
  {
    id: 'actions',
    headCell: <ActionHeadCell padding="16px" minWidth="97px" />,
    cell: (
      <PermissionGroupActionCell
        padding="16px"
        onSelectAction={onSelectAction}
        access={access}
        adminPermissionGroup={adminPermissionGroup}
      />
    ),
  },
];
